"use client";

import { DrawerPreview as Drawer } from "@base-ui/react/drawer";
import React, { useCallback, useState } from "react";

import { CloseIcon } from "../dx-icons";

import { RoomDetailBody } from "./body/room-detail-body";
import { DETAIL_DRAWER_POPUP_HEIGHT_CLASS } from "./detail-drawer-layout";
import type { RoomDetailDrawerPayload } from "./room-detail-drawer-provider";

// Drawer Stack 表現 (https://base-ui.com/react/components/drawer#nested-drawers):
// ネスト Drawer が開くと Base-UI が --nested-drawers (深さ) を自動供給する。公式デモと同様に
// 1 枚につき --peek ぶん上へずらして上端を覗かせ、--stack-step ぶん縮める。origin-top なので
// 上端の覗き位置がそのまま決まり、デモの bleed / 高さ補正は不要。--stack-progress (ネスト側の
// スワイプ進捗) でスワイプ中に滑らかにスタックが解ける。
// SP は上の隙間がほぼ無く、上へずらすと上端が画面外に出る。--peek を 0 にして縮小だけ残す。
const DRAWER_STACK_CLASS =
  "[--peek:0px] md:[--peek:16px] [--stack-peek-offset:max(0px,calc((var(--nested-drawers,0)-var(--stack-progress))*var(--peek)))] [--stack-progress:clamp(0,var(--drawer-swipe-progress,0),1)] [--stack-scale:clamp(0,calc(max(0,1-(var(--nested-drawers,0)*var(--stack-step)))+(var(--stack-step)*var(--stack-progress))),1)] [--stack-step:0.05] data-nested-drawer-swiping:duration-0";

const DRAWER_STACK_TRANSFORM =
  "translateY(calc(var(--drawer-snap-point-offset, 0px) + var(--drawer-swipe-movement-y, 0px) - var(--stack-peek-offset, 0px))) scale(var(--stack-scale, 1))";

export function RoomDetailDrawer({
  payload,
  onClose,
}: {
  payload: RoomDetailDrawerPayload | null;
  onClose: () => void;
}) {
  // ネスト Drawer は accommodation 経路のみ（roomType 経路はカレンダーを常時インライン描画する）。
  // 親 Drawer を閉じる時は必ずネストも閉じる (閉じ忘れによる leak 防止)。
  const [nestedOpen, setNestedOpen] = useState(false);
  const closeAll = useCallback(() => {
    setNestedOpen(false);
    onClose();
  }, [onClose]);
  // PC Popover のアンカー用 (accommodation 経路のみ)。
  // useRef + getAnchorElement は React Compiler ESLint (react-hooks/refs) が
  // "Cannot access refs during render" を報告するため、useState + callback ref で代替する。
  // anchorEl は通常の state なので render 中に読み取っても ESLint の検知外になる。
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  // anchorEl は state なので render 中に読んで render prop に渡してよい。
  // 関数ラッパーにすると Base-UI Popover が anchor を再解決せず効かないため、要素を直接渡す。
  const nestedDrawerNode =
    payload != null && "accommodationId" in payload
      ? payload.availability?.nestedDrawer.render({
          open: nestedOpen,
          onOpenChange: setNestedOpen,
          closeAll,
          anchorEl,
        })
      : undefined;

  return (
    <Drawer.Root
      open={payload !== null}
      onOpenChange={(open) => {
        if (!open) closeAll();
      }}
    >
      <Drawer.Portal>
        <Drawer.Backdrop className="fixed inset-0 z-50 bg-black/40 transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0" />
        <Drawer.Viewport className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-[640px]">
          <Drawer.Popup
            data-testid="room-detail-drawer"
            // safe-area は Popup 自身が吸収することで、loading/error など CTA が無い状態でも
            // 末尾コンテンツが iOS Home Indicator に被らないようにする。
            // スタック表現の math は DRAWER_STACK_CLASS / DRAWER_STACK_TRANSFORM を参照。
            className={`bg-dx-surface-base flex w-full origin-top flex-col overflow-hidden rounded-t-[16px] pb-[env(safe-area-inset-bottom)] shadow-lg transition-transform duration-200 data-ending-style:translate-y-full data-starting-style:translate-y-full ${DETAIL_DRAWER_POPUP_HEIGHT_CLASS} ${DRAWER_STACK_CLASS}`}
            style={{ transform: DRAWER_STACK_TRANSFORM }}
          >
            {/* grip bar */}
            <div className="flex shrink-0 justify-center pt-[12px] pb-[8px]">
              <div className="bg-dx-border-default h-[4px] w-[40px] rounded-full" />
            </div>
            {/* close button: Hero Image の右上に重ねる (grip bar 24px の下 + 8px)。
                right-[28px] は デスクトップのスクロールバー幅 (~17px) + 余白 (~11px) を
                Popup 右端から確保する分。scrollbar に被らない位置に置く意図。
                z-20: 本体スクロール領域内の sticky 要素 (曜日ヘッダー/月見出し/FeeOptionCards/CTA、
                いずれも z-10 以下) より DOM 順で後にレンダリングされても常に手前に来る必要がある。
                Drawer.Backdrop/Viewport の z-50 (モーダル同士の重なり制御) とは別の文脈。 */}
            <Drawer.Close
              className="bg-dx-surface-base text-dx-text-default absolute top-[32px] right-[28px] z-20 flex size-[32px] items-center justify-center rounded-full"
              aria-label="閉じる"
            >
              <CloseIcon />
            </Drawer.Close>
            {/* scrollable body — 内部セクションが自前で px を持つので Drawer 側では付けない。
                flex-1 min-h-0 が無いと flex-basis: auto のままコンテンツ量に応じて伸び、
                Popup の max-height を超えた分が overflow-hidden で物理的に切れてしまう
                （overflow-y-auto 自体は効くがスクロール可能な余白が生まれない）。 */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {payload && (
                <RoomDetailBody
                  payload={payload}
                  onCloseDrawer={closeAll}
                  onOpenNestedDrawer={() => setNestedOpen(true)}
                  onCtaMount={setAnchorEl}
                />
              )}
            </div>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
      {/* ネスト Drawer は親 Drawer.Root の React ツリー内 (子孫) に宣言する必要がある
          (Base-UI のネストは context 依存のため createPortal では成立しない) */}
      {nestedDrawerNode}
    </Drawer.Root>
  );
}
